import React, { useState, useEffect } from "react";
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";

import { displayNotification } from "../../commonComponents/notifications/displayNotification";

import Body from "../../commonComponents/body/Body";
import ReactTable from "../../commonComponents/reactTable/ReactTable";
import { generateReportHeaders } from "../../utils/table.utils";
import {
  bulkUploadAPI,
  createNewRecord,
  getAllDataV5,
  getHeaders,
  updateRecord,
} from "../../services/common.service";
import ActionButton from "../../commonComponents/components/actionButton/ActionButton";
import EditModal from "../../commonComponents/components/editModal/EditModal";
import { patchAPICall, postAPICall } from "../../services/apiCall";

const source = "combo_products";
let defaultSorting = { id: "id", desc: false };
const ComboProduct = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    search: "",
    source,
    sort: [defaultSorting],
  });
  const [editModal, setEditModal] = useState();
  const queryClient = new QueryClient();

  const allRecordsQuery = useQuery({
    queryKey: ["all-records", source, pagination],
    queryFn: () => getAllDataV5({ ...pagination, source: "combo-products" }),
  });

  const tableHeadersQuery = useQuery({
    queryKey: ["table-headers", source],
    queryFn: () => getHeaders({ source }),
    select: (data) => {
      return { headers: generateReportHeaders(data?.[0]), data: data?.[0] };
    },
  });

  const uploadDataQuery = useMutation({
    mutationFn: (body) => bulkUploadAPI({ body, tableName: source }),
  });

  const createDataQuery = useMutation({
    mutationFn: (body) => postAPICall(`combo-products`, { body }),
  })

  const updateDataQuery = useMutation({
    mutationFn: (body) =>
      patchAPICall(`combo-products/${body.id}`, { body }),
  });

  useEffect(() => {
    if (pagination?.search) {
      setPagination((old) => ({ ...old, page: 1 }));
    }
  }, [pagination?.search]);

  const handleSorting = (e) => {
    // find the sorting data in the array from previous state
    const d = e(pagination.sort);
    setPagination((old) => ({ ...old, sort: d }));
  };

  return (
    <>
      <Body>
        <ActionButton
          title={"Combo Product"}
          builderTitle={"Add New Combo Product"}
          pagination={pagination}
          setPagination={setPagination}
          actionName={source}
          saveLoading={createDataQuery?.isPending}
          tableName={source}
          onSave={(e, state) =>
            createDataQuery?.mutate(e?.data, {
              onSuccess: () => {
                displayNotification({
                  message: "New Combo Product Added Successfully",
                  variant: "success",
                });
                state({});
                allRecordsQuery?.refetch();
              },
              onError: (err) => {
                displayNotification({
                  message: err?.message || err,
                  variant: "error",
                });
              },
            })
          }
          builderData={tableHeadersQuery?.data?.data}
          uploadDataQuery={uploadDataQuery}
          queryKey={"combo_product-list"}
        />
        <ReactTable
          sorting={pagination.sort}
          setSorting={handleSorting}
          columnData={tableHeadersQuery?.data?.headers}
          loading={allRecordsQuery?.isFetching || tableHeadersQuery?.isLoading}
          onRowClick={(e) => setEditModal({ data: e, modal: true })}
          totalNoOfPages={allRecordsQuery?.data?.metadata?.total_number_of_page}
          totalNoOfRecords={allRecordsQuery?.data?.metadata?.records}
          rowData={allRecordsQuery?.data?.data}
          page={pagination?.page}
          setPage={(page) => setPagination({ ...pagination, page })}
        />

        <EditModal
          size="md"
          formData={editModal?.data}
          opened={editModal?.modal}
          onClose={() => setEditModal({ modal: false })}
          builderTitle={"Edit Combo Product"}
          saveLoading={updateDataQuery?.isPending}
          tableName={source}
          onSave={(e, state) =>
            updateDataQuery?.mutate(e, {
              onSuccess: () => {
                displayNotification({
                  message: "Combo Product Updated Successfully",
                  variant: "success",
                });
                setEditModal({ modal: false });
                // queryClient.invalidateQueries(['all-records', source, pagination]);
                allRecordsQuery?.refetch();
              },
              onError: (err) => {
                displayNotification({
                  message: err?.message || err,
                  variant: "error",
                });
              },
            })
          }
        />
      </Body>
    </>
  );
};

export default ComboProduct;
